import React, { useMemo, useState } from 'react';

import Badge from '@amzn/meridian/badge';
import Button from '@amzn/meridian/button';
import Modal from '@amzn/meridian/modal';
import RadioButton from '@amzn/meridian/radio-button';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';

import { isUndefined } from 'lodash';
import { ITablePicker } from 'src/components/CompareContracts/TablePickers/table-picker-props';
import {
    ExtractResult,
    Lookup,
    TableResult,
    TableRow,
    createLookup,
    createStack
} from 'src/components/CompareContracts/utils/comparison-extractor';
import { getChanges } from 'src/components/CompareContracts/utils/comparison-utils';

interface ITreeList {
    table: string;
    lookup: Lookup;
    depth?: number;
    extract: ExtractResult;
    selected: string;
    setSelected: (selected: string | undefined) => void;
}

/**
 * Render a nested list of radio buttons for table selection
 * @param table The root table of this list
 * @param extract The extracted comparison data.
 * @param lookup Maps each table to its nested child tables.
 * @param selected The selected table.
 * @param setSelected Set the selected table.
 * @param depth The nesting depth of the list. Default is 0.
 */
const TreeList: React.FC<ITreeList> = ({ table, extract, lookup, selected, setSelected, depth = 0 }) => {
    const children = lookup[table]?.children ?? [];
    const hasChildren = children.length > 0;
    const numChanges = getChanges(extract[table]);

    return (
        <>
            <li style={{ display: 'block' }}>
                <RadioButton checked={table === selected} onChange={() => setSelected(table)}>
                    {table}
                    {numChanges > 0 && <Badge value={numChanges} />}
                </RadioButton>
            </li>
            {hasChildren && (
                <Text tag={'ul'}>
                    {children.map((child) => (
                        <TreeList
                            key={child}
                            table={child}
                            depth={depth + 1}
                            lookup={lookup}
                            extract={extract}
                            selected={selected}
                            setSelected={setSelected}
                        />
                    ))}
                </Text>
            )}
        </>
    );
};

/**
 * Select tables from a nested list which reflects the tree of relationships
 * between the tables.
 * @param props The table picker props
 */
export const TreeTablePicker: React.FC<ITablePicker> = (props) => {
    const [isOpen, setIsOpen] = useState(false);
    const lookup = useMemo(() => createLookup(props.comparison), [props.comparison]);
    const stack = useMemo(() => createStack(lookup, props.table), [lookup, props.table]);
    const rootTable = stack.length > 0 ? stack[0] : '';

    return (
        <Row widths={'fit'}>
            <Button onClick={() => setIsOpen(true)}>Show Tables</Button>
            <Modal open={isOpen} title={'Tables'} onClose={() => setIsOpen(false)}>
                <Text tag={'ul'}>
                    <TreeList
                        table={rootTable}
                        lookup={lookup}
                        extract={props.extract}
                        selected={props.table}
                        setSelected={props.setTable}
                    />
                </Text>
            </Modal>
        </Row>
    );
};
